import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@/lib/api";
import { getAvailableSlots } from "@/lib/onboarding";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Users } from "@/icons/Icon";
import { DeliveryRateImprovedModal } from "@/components/organisms/DeliveryRateImprovedModal";
import { OnboardingForm } from "./OnboardingForm";
import { CalendlyStep } from "./CalendlyStep";
import { useOnboarding } from "./OnboardingProvider";
import {
  useAvailableSlotsQuery,
  useRecordCalendlyBookingMutation,
  useSubmitOnboardingFormMutation,
} from "./queries";
import type { OnboardingFormInput } from "./schemas";
import { deriveInitialView, viewAfterFormSubmit, type View } from "./onboarding-modal-helpers";
import { trackOnboardingAnswersCaptured } from "./onboarding-analytics";

// The form view is a fixed head / scrolling body / fixed foot card, so it drops
// DialogContent's own grid, gap and padding; the form's sections pad themselves.
const ONBOARDING_FORM_DIALOG_CLASS =
  "flex max-h-[min(86vh,760px)] max-w-[520px] flex-col gap-0 overflow-hidden rounded-[var(--r-xl)] border-[color:var(--ink-200)] bg-[color:var(--paper)] p-0 shadow-[shadow:var(--sh-pop)] sm:rounded-[var(--r-xl)]";

export function OnboardingModal() {
  const {
    status,
    loading,
    error: contextError,
    refresh,
    applyStatus,
  } = useOnboarding();

  const [view, setView] = useState<View>("closed");
  const [formError, setFormError] = useState<string | null>(null);
  const sessionStarted = useRef(false);

  // Initial view decided once after the status query first settles. Subsequent
  // refreshes don't pop the modal open again — that's a one-shot per session.
  useEffect(() => {
    if (sessionStarted.current) return;
    if (loading) return;
    if (!status) return;
    sessionStarted.current = true;
    setView(deriveInitialView(status));
  }, [loading, status]);

  const slotsQuery = useAvailableSlotsQuery(undefined, view === "calendly");
  const bookingMutation = useRecordCalendlyBookingMutation();
  const submitMutation = useSubmitOnboardingFormMutation();

  const showError = !loading && !status && contextError !== null;
  const isOpen = view === "form" || view === "calendly" || showError;

  const title = useMemo(() => {
    if (showError) return "Something went wrong";
    if (view === "form") return "Tell us about your team";
    return "Let's Get You Started! 🎉";
  }, [showError, view]);

  const description = useMemo(() => {
    if (showError) return "We couldn't load your onboarding state.";
    if (view === "form")
      return "A few quick questions so we can tailor your warmup setup.";
    return "95% of our customers get better deliverability after a consultation call. Book from here 👇";
  }, [showError, view]);

  async function handleSubmit(payload: OnboardingFormInput) {
    setFormError(null);
    try {
      const next = await submitMutation.mutateAsync(payload);
      applyStatus(next);
      trackOnboardingAnswersCaptured(payload);
      setView(viewAfterFormSubmit(next));
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : "Could not save your answers. Please try again.",
      );
    }
  }

  function handleCalendlyDone() {
    setView("deliveryInfo");
    void refresh();
  }

  function handleDeliveryInfoClose() {
    setView("closed");
  }

  if (!isOpen && view !== "deliveryInfo") return null;

  return (
    <>
      {isOpen && (
        <Dialog
          open
          onOpenChange={(open) => {
            // The modal is not user-dismissible — onboarding is required.
            // Radix may still try to close on Escape; ignore it.
            if (!open) {
              /* no-op */
            }
          }}
        >
          <DialogContent
            className={cn(
              "max-w-lg",
              view === "calendly" && "max-w-[57rem]",
              view === "form" && ONBOARDING_FORM_DIALOG_CLASS,
            )}
            showCloseButton={false}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            {view === "form" ? (
              <div className="onboarding-head">
                <span className="onboarding-badge" aria-hidden>
                  <Users size={19} />
                </span>
                <div className="min-w-0">
                  <DialogTitle className="text-[18px] font-extrabold leading-snug tracking-[-0.02em] text-[color:var(--ink-900)]">
                    {title}
                  </DialogTitle>
                  <DialogDescription className="mt-[3px] text-[13px] leading-normal text-[color:var(--ink-500)]">
                    {description}
                  </DialogDescription>
                </div>
              </div>
            ) : (
              <DialogHeader
                className={
                  view === "calendly" ? "text-center sm:text-center" : undefined
                }
              >
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
              </DialogHeader>
            )}

            {showError && (
              <>
                <p
                  className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3 mb-4"
                  role="alert"
                >
                  {contextError}
                </p>
                <Button onClick={() => void refresh()} size="lg">
                  Try again
                </Button>
              </>
            )}

            {view === "form" && (
              <OnboardingForm
                onSubmit={(p) => void handleSubmit(p)}
                submitting={submitMutation.isPending}
                error={formError}
              />
            )}

            {view === "calendly" && status && (
              <CalendlyStep
                calendlyUrl={status.calendlyUrl}
                slots={slotsQuery.data ?? []}
                loadingSlots={slotsQuery.isPending}
                slotsError={slotsQuery.error}
                onRetrySlots={() => void slotsQuery.refetch()}
                bookingMutation={bookingMutation}
                fetchMoreSlots={(cursor) => getAvailableSlots(cursor)}
                onBooked={handleCalendlyDone}
                onSkip={handleCalendlyDone}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
      <DeliveryRateImprovedModal
        open={view === "deliveryInfo"}
        onOpenChange={(open) => {
          if (!open) handleDeliveryInfoClose();
        }}
      />
    </>
  );
}
